import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { INK, MUTED_36, FONT, ROOMS, bubbleRight, glow, capsuleStyle } from "@/lib/yuoDesign";
import RoomShell from "@/components/yuo/RoomShell";
import InputRow from "@/components/yuo/InputRow";
import { ConversationHistoryModal } from "@/components/ConversationHistoryModal";
import { UpgradeSheet } from "@/components/UpgradeSheet";
import { HighlightedText } from "@/components/HighlightedText";
import { useResponsive } from "@/lib/responsive";
import { MobileHeaderButton } from "@/components/mobile/MobileHeader";

const MODES = [
  { key: "feeling", label: "Feeling", hex: "#ff7a62", rgb: "255,122,98" },
  { key: "craft",   label: "Craft",   hex: "#b98cff", rgb: "185,140,255" },
  { key: "growth",  label: "Growth",  hex: "#5cf0a6", rgb: "92,240,166" },
];

// v6 mobile: visible semantic row — one active (client UI state only)
function ModeRow({ mode, onChange }) {
  return (
    <div data-testid="mirror-mode-row" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 6, padding: "0 4px 10px" }}>
      {MODES.map((m, i) => {
        const active = mode === i;
        return (
          <button key={m.key} type="button" data-testid={`mirror-mode-${m.key}`} onClick={() => onChange(i)} style={{
            height: 34, borderRadius: 999, border: "none", cursor: "pointer", fontFamily: FONT,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
            fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase",
            background: active ? `rgba(${m.rgb},0.18)` : "var(--m-more-bg)",
            boxShadow: active ? `inset 0 0 0 1px rgba(${m.rgb},0.45)` : "none",
            color: active ? m.hex : "var(--yuo-ink)",
          }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: m.hex, boxShadow: `0 0 8px rgba(${m.rgb},0.7)` }} />
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

const HIGHLIGHT_RGB_FOR_KIND = { coral: "255,157,143", purple: "195,185,245", green: "138,255,196" };

export default function Mirror() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [voiceMeta, setVoiceMeta] = useState({ active: false });
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(null);
  const [savedWord, setSavedWord] = useState(null);
  const listEndRef = useRef(null);

  const saveWordToDiary = async (word, kind) => {
    try {
      await api.createDiary({ title: `Mirror: ${word}`, content: `[${kind}] ${word}` });
      setSavedWord(word);
      setTimeout(() => setSavedWord(null), 1800);
    } catch (e) { console.warn(e); }
  };

  const loadConversations = useCallback(async () => {
    const convs = await api.listConversations("mirror");
    setConversations(convs || []);
    return convs || [];
  }, []);

  const loadMessages = useCallback(async (cid) => {
    const data = await api.getConversation(cid);
    setMessages(data.messages || []);
  }, []);

  const loadLatest = useCallback(async () => {
    setLoading(true);
    try {
      const convs = await loadConversations();
      if (convs.length > 0) {
        const current = conversationId && convs.find((c) => c.conversation_id === conversationId);
        const target = current ? current.conversation_id : convs[0].conversation_id;
        setConversationId(target);
        await loadMessages(target);
      } else {
        setConversationId(null);
        setMessages([]);
      }
    } catch (e) { console.warn(e); }
    finally { setLoading(false); }
  }, [conversationId, loadConversations, loadMessages]);

  useEffect(() => { loadLatest(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages]);

  const ensureConversation = async () => {
    if (conversationId) return conversationId;
    try {
      const conv = await api.createConversation("mirror");
      setConversationId(conv.conversation_id);
      await loadConversations();
      return conv.conversation_id;
    } catch (e) {
      if (e?.status === 402) setShowUpgrade("A second Mirror chat requires YUO Pro.");
      throw e;
    }
  };

  const send = async () => {
    const content = text.trim();
    if (!content || sending) return;
    setSending(true);
    const optimistic = { message_id: `tmp_${Date.now()}`, role: "user", content };
    try {
      const cid = await ensureConversation();
      setMessages((p) => [...p, optimistic]);
      const voiceFlag = voiceMeta.active; const voiceLang = voiceMeta.language || null;
      setText(""); setVoiceMeta({ active: false });
      const res = await api.sendMessage(cid, content, undefined, { via_voice: voiceFlag, voice_language: voiceLang });
      setMessages((p) => {
        const seen = new Set([res.user_message.message_id, res.assistant_message.message_id]);
        const w = p.filter((m) => m.message_id !== optimistic.message_id && !seen.has(m.message_id));
        return [...w, res.user_message, res.assistant_message];
      });
      await loadConversations();
    } catch (e) {
      if (e?.status === 402) setMessages((p) => p.filter((m) => m.message_id !== optimistic.message_id));
      else {
        const errText = e?.status === 0 ? "Could not reach YUO. Check your connection and try again." : e?.message || "No reply arrived.";
        setMessages((p) => {
          const withoutTmp = p.filter((m) => m.message_id !== optimistic.message_id);
          return [...withoutTmp, optimistic, { message_id: `err_${Date.now()}`, role: "assistant", content: errText }];
        });
      }
    } finally { setSending(false); }
  };

  const startNewChat = async () => {
    try {
      const conv = await api.createConversation("mirror");
      setConversationId(conv.conversation_id);
      setMessages([]);
      await loadConversations();
    } catch (e) {
      if (e?.status === 402) setShowUpgrade("A second Mirror chat requires YUO Pro.");
    }
  };

  const openChat = async (cid) => {
    setConversationId(cid); setMessages([]); setLoading(true);
    try { await loadMessages(cid); } finally { setLoading(false); }
  };

  const removeChat = async (cid) => {
    await api.deleteConversation(cid);
    const convs = await loadConversations();
    if (cid === conversationId) {
      if (convs.length > 0) { setConversationId(convs[0].conversation_id); await loadMessages(convs[0].conversation_id); }
      else { setConversationId(null); setMessages([]); }
    }
  };

  const showLegend = messages.some((m) => m.role === "assistant" && /\[\[(coral|purple|green):/.test(m.content));

  const { isPhone } = useResponsive();
  const [mode, setMode] = useState(0);
  const mobileActions = (
    <MobileHeaderButton room="mirror" solid onClick={startNewChat} testId="mirror-new-chat-m">+ New chat</MobileHeaderButton>
  );
  const moreRows = [
    { label: `History · ${conversations.length}`, hint: "past conversations", color: "mirror", right: "›", testId: "m-mirror-history", onClick: () => setShowHistory(true) },
    { label: user?.name?.split(" ")[0] || "You", hint: "profile · type · plan", color: "diary", right: "›", testId: "m-mirror-profile", onClick: () => navigate("/profile") },
  ];
  const actions = (
    <>
      <ThemeLegend />
      <button data-testid="mirror-history-button" onClick={() => setShowHistory(true)} style={capsuleStyle({ active: false })}>
        History · {conversations.length}
      </button>
      <button data-testid="mirror-new-chat" onClick={startNewChat} style={capsuleStyle({ active: false, room: "mirror" })}>
        + New chat
      </button>
      <button data-testid="mirror-personalisation-button" onClick={() => navigate("/profile")}
        style={{
          ...capsuleStyle({ active: !!user?.personalization_profile, room: "diary" }),
          padding: user?.personalization_profile ? "4px 14px 4px 4px" : "8px 18px",
          gap: 10,
        }}>
        {user?.personalization_profile && (
          <span style={{
            width: 26, height: 26, borderRadius: "50%",
            background: `radial-gradient(38% 34% at 34% 26%, #fff, ${user.personalization_profile.body_color} 22%, #201005 60%, #01040a 100%)`,
            boxShadow: `0 0 10px ${user.personalization_profile.body_color}88`,
          }} />
        )}
        <span>{user?.name?.split(" ")[0] || "You"}</span>
      </button>
    </>
  );

  return (
    <RoomShell
      room="mirror"
      title="Mirror"
      subtitle="conversation"
      actions={actions}
      mobileActions={mobileActions}
      moreRows={moreRows}
      moreTitle="Mirror · more"
      testId="mirror-screen"
      subheader={<ModeRow mode={mode} onChange={setMode} />}
      footer={
        <InputRow
          room="mirror"
          value={text}
          onChange={(v) => { setText(v); if (!v.trim()) setVoiceMeta({ active: false }); }}
          onSend={send}
          placeholder="speak…"
          inputTestId="mirror-input"
          sendTestId="mirror-send-button"
          askYuoTestId="ask-yuo"
          onAskYuo={() => setText((p) => p.trim() ? `${p}\n\nYUO, ?` : "YUO, ?")}
        />
      }
    >
      <div style={{ maxWidth: 1060, margin: "0 auto", display: "flex", flexDirection: "column", gap: isPhone ? 14 : 22 }}>
        {loading && messages.length === 0 ? (
          <div style={{ color: MUTED_36, fontSize: 11, letterSpacing: "0.32em", textAlign: "center", padding: 30, textTransform: "uppercase" }}>
            listening…
          </div>
        ) : (
          messages.map((m) => <Bubble key={m.message_id} msg={m} onWordPress={saveWordToDiary} />)
        )}
        <div ref={listEndRef} />
      </div>

      <ConversationHistoryModal
        visible={showHistory} onClose={() => setShowHistory(false)}
        items={conversations} activeId={conversationId} accent={ROOMS.mirror.hex}
        onSelect={openChat} onNew={startNewChat} onDelete={removeChat}
      />
      <UpgradeSheet visible={!!showUpgrade} reason={showUpgrade || ""} isPro={user?.plan === "pro"} onClose={() => setShowUpgrade(null)} />

      {savedWord && (
        <div data-testid="word-saved-toast" style={{
          position: "fixed", bottom: 110, left: "50%", transform: "translateX(-50%)",
          background: `rgba(${ROOMS.diary.rgb},0.12)`, boxShadow: `0 0 22px rgba(${ROOMS.diary.rgb},0.28)`,
          padding: "10px 18px", borderRadius: 999, color: INK, fontFamily: FONT, fontSize: 12, letterSpacing: "0.14em",
        }}>
          saved "{savedWord}" to Diary
        </div>
      )}
    </RoomShell>
  );
}

function ThemeLegend() {
  const dot = (rgb) => ({
    width: 8, height: 8, borderRadius: "50%",
    background: `rgb(${rgb})`, boxShadow: `0 0 10px rgba(${rgb},0.8)`,
    display: "inline-block",
  });
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 14, marginRight: 6,
      padding: "8px 16px", borderRadius: 999,
      background: "var(--fg-05)",
      fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--fg-6)",
    }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={dot("255,157,143")} />feeling</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={dot("195,185,245")} />craft</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span style={dot("138,255,196")} />growth</span>
    </div>
  );
}

function Bubble({ msg, onWordPress }) {
  const isUser = msg.role === "user";
  if (isUser) {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <div style={{
          maxWidth: "84%", padding: "18px 22px", borderRadius: 26,
          background: bubbleRight(ROOMS.mirror.rgb, 0.22),
          fontSize: 13.5, lineHeight: 1.65, color: INK, whiteSpace: "pre-wrap",
          animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both",
        }}>
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div style={{
      padding: "22px 24px", borderRadius: 28,
      background: glow(ROOMS.symbiosis.rgb, 0.16),
      fontSize: 13.5, lineHeight: 1.7, color: "var(--lit-green-2)",
      animation: "yuo-emerge 1.1s cubic-bezier(.2,.7,.2,1) both",
    }}>
      <HighlightedText content={msg.content} onWordPress={onWordPress}
        baseStyle={{ color: "var(--lit-green-2)", fontFamily: FONT, fontSize: 13.5, lineHeight: 1.7 }} />
    </div>
  );
}
